/**
 * Floats UI Components
 * ====================
 * Core component library built on Base UI
 */

// Button
export { Button } from './Button';
export type { ButtonProps } from './Button';

// ConfirmButton
export { ConfirmButton } from './ConfirmButton';
export type { ConfirmButtonProps } from './ConfirmButton';

// SplitButton
export { SplitButton } from './SplitButton';
export type { SplitButtonProps, SplitButtonItem } from './SplitButton';

// IconButton
export { IconButton } from './IconButton';
export type { IconButtonProps } from './IconButton';

// PdfFileIcon
export { PdfFileIcon } from './PdfFileIcon';
export type { PdfFileIconProps } from './PdfFileIcon';

// Input
export { Input } from './Input';
export type { InputProps } from './Input';

// Textarea
export { Textarea } from './Textarea';
export type { TextareaProps } from './Textarea';

// ColorField
export { ColorField } from './ColorField';
export type { ColorFieldProps } from './ColorField';

// Select
export { Select } from './Select';
export type { SelectProps, SelectOption } from './Select';

// Autocomplete
export { Autocomplete } from './Autocomplete';
export type { AutocompleteProps } from './Autocomplete';

// Checkbox
export { Checkbox } from './Checkbox';
export type { CheckboxProps } from './Checkbox';

// Switch
export { Switch } from './Switch';
export type { SwitchProps } from './Switch';

// Radio
export { RadioGroup, Radio } from './Radio';
export type { RadioGroupProps, RadioProps } from './Radio';

// Slider
export { Slider } from './Slider';
export type { SliderProps } from './Slider';

// Card
export {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from './Card';
export type { CardProps, CardHeaderProps, CardTitleProps, CardDescriptionProps, CardContentProps, CardFooterProps } from './Card';

// Tag
export { Tag, MetricBadge } from './Tag';
export type { TagProps, MetricBadgeProps } from './Tag';

// TagFilter
export { TagFilter } from './TagFilter';
export type { TagFilterProps, TagFilterOption } from './TagFilter';

// Filters
export { Filters, createFilter } from './Filters';
export type { FiltersProps, Filter, FilterFieldConfig, FilterFieldGroup, FilterFieldsInput, FilterOption, FilterOperator } from './Filters';

// FiltersMenu
export { FiltersMenu } from './FiltersMenu';
export type { FiltersMenuProps } from './FiltersMenu';

// Dialog
export {
  Dialog,
  DialogTrigger,
  DialogPortal,
  DialogBackdrop,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
  DialogClose,
} from './Dialog';
export type {
  DialogProps,
  DialogTriggerProps,
  DialogPortalProps,
  DialogBackdropProps,
  DialogContentProps,
  DialogHeaderProps,
  DialogTitleProps,
  DialogDescriptionProps,
  DialogBodyProps,
  DialogFooterProps,
  DialogCloseProps,
} from './Dialog';

// SideSheet
export {
  SideSheet,
  SideSheetTrigger,
  SideSheetPortal,
  SideSheetBackdrop,
  SideSheetContent,
  SideSheetHeader,
  SideSheetTitle,
  SideSheetDescription,
  SideSheetBody,
  SideSheetFooter,
  SideSheetClose,
} from './SideSheet';
export type {
  SideSheetProps,
  SideSheetTriggerProps,
  SideSheetPortalProps,
  SideSheetBackdropProps,
  SideSheetContentProps,
  SideSheetHeaderProps,
  SideSheetTitleProps,
  SideSheetDescriptionProps,
  SideSheetBodyProps,
  SideSheetFooterProps,
  SideSheetCloseProps,
} from './SideSheet';

// Tabs
export { Tabs, TabList, Tab, TabPanel } from './Tabs';
export type { TabsProps, TabListProps, TabProps, TabPanelProps } from './Tabs';

// SegmentedControl
export { SegmentedControl } from './SegmentedControl';
export type { SegmentedControlProps, SegmentedControlOption } from './SegmentedControl';

// Accordion
export { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from './Accordion';
export type { AccordionProps, AccordionItemProps, AccordionTriggerProps, AccordionContentProps } from './Accordion';

// Tooltip
export { Tooltip } from './Tooltip';
export type { TooltipProps } from './Tooltip';

// Toast
export { Toast, ToastContainer } from './Toast';
export type { ToastProps } from './Toast';

// Alert
export { Alert } from './Alert';
export type { AlertProps } from './Alert';

// Avatar
export { Avatar, AvatarGroup, getInitials, getColorFromName } from './Avatar';
export type { AvatarProps, AvatarGroupProps } from './Avatar';

// Breadcrumbs
export { Breadcrumbs } from './Breadcrumbs';
export type { BreadcrumbsProps, BreadcrumbItem } from './Breadcrumbs';

// Spinner
export { Spinner } from './Spinner';
export type { SpinnerProps } from './Spinner';

// Progress
export { Progress } from './Progress';
export type { ProgressProps } from './Progress';

// Stepper
export { Stepper } from './Stepper';
export type { StepperProps, Step } from './Stepper';

// QualityScore
export { QualityScore } from './QualityScore';
export type { QualityScoreProps } from './QualityScore';

// Skeleton
export { Skeleton, SkeletonText, SkeletonAvatar, SkeletonCard } from './Skeleton';
export type { SkeletonProps } from './Skeleton';

// Table
export { Table, TableHead, TableBody, TableRow, TableHeader, TableCell, TableActionCell, TableLink } from './Table';
export type { TableProps, TableHeadProps, TableBodyProps, TableRowProps, TableHeaderProps, TableCellProps, TableActionItem, TableActionCellProps, TableLinkProps } from './Table';

// Pagination
export { Pagination } from './Pagination';
export type { PaginationProps } from './Pagination';

// WelcomePanel
export { WelcomePanel } from './WelcomePanel';
export type { WelcomePanelProps, WelcomePanelProfileProps } from './WelcomePanel';

// MetricCard
export { MetricCard } from './MetricCard';
export type { MetricCardProps } from './MetricCard';

// UploadCard
export { UploadCard } from './UploadCard';
export type { UploadCardProps, UploadCardTipProps } from './UploadCard';

// NotificationList
export { NotificationList } from './NotificationList';
export type { NotificationListProps, NotificationItemProps } from './NotificationList';

// LeadList
export { LeadList } from './LeadList';
export type { LeadListProps, LeadItemProps } from './LeadList';

// CandidateSelectList
export { CandidateSelectList } from './CandidateSelectList';
export type { CandidateSelectListProps, CandidateSelectListItem } from './CandidateSelectList';

// CandidateProfileCard
export { CandidateProfileCard } from './CandidateProfileCard';
export type { CandidateProfileCardProps } from './CandidateProfileCard';

// CandidateCardAvatar
export { CandidateCardAvatar } from './CandidateCardAvatar';
export type { CandidateCardAvatarProps } from './CandidateCardAvatar';

// Avatar sources
export { CANDIDATE_AVATAR_PLACEHOLDER } from './candidatePlaceholder';
export { GENERIC_AVATAR_SRC, isLegacyGravatarUrl, isStandInAvatar } from './avatarSource';

// CandidateCardProfile
export { CandidateCardProfile } from './CandidateCardProfile';
export type { CandidateCardProfileProps } from './CandidateCardProfile';

// CandidateCardProfessional
export { CandidateCardProfessional } from './CandidateCardProfessional';
export type { CandidateCardProfessionalProps } from './CandidateCardProfessional';

// CandidateCardStartup
export { CandidateCardStartup } from './CandidateCardStartup';
export type { CandidateCardStartupProps } from './CandidateCardStartup';

// CandidateCardSpotlight
export { CandidateCardSpotlight } from './CandidateCardSpotlight';
export type { CandidateCardSpotlightProps } from './CandidateCardSpotlight';

// CandidateCardLight
export { CandidateCardLight } from './CandidateCardLight';
export type { CandidateCardLightProps } from './CandidateCardLight';

// CandidateCardSnow
export { CandidateCardSnow } from './CandidateCardSnow';
export type { CandidateCardSnowProps } from './CandidateCardSnow';

// CandidateCardMultiCorporate
export { CandidateCardMultiCorporate } from './CandidateCardMultiCorporate';
export type { CandidateCardMultiCorporateProps } from './CandidateCardMultiCorporate';

// CandidateCardMultiProfessional
export { CandidateCardMultiProfessional } from './CandidateCardMultiProfessional';
export type { CandidateCardMultiProfessionalProps } from './CandidateCardMultiProfessional';

// JobCard
export { JobCard } from './JobCard';
export type { JobCardProps } from './JobCard';

// JobCardStartup
export { JobCardStartup } from './JobCardStartup';
export type { JobCardStartupProps } from './JobCardStartup';

// JobCardSpotlight
export { JobCardSpotlight } from './JobCardSpotlight';
export type { JobCardSpotlightProps } from './JobCardSpotlight';

// JobCardLight
export { JobCardLight } from './JobCardLight';
export type { JobCardLightProps } from './JobCardLight';

// JobCardSnow
export { JobCardSnow } from './JobCardSnow';
export type { JobCardSnowProps } from './JobCardSnow';

// Reports primitives
export { Block } from './Block';
export type { BlockProps, BlockChipsContext, BlockAction, BlockDensity } from './Block';

export { DashboardGrid } from './DashboardGrid';
export type { DashboardGridProps, DashboardGridLayoutItem } from './DashboardGrid';

export { KpiCard } from './KpiCard';
export type { KpiCardProps, KpiColor, KpiFormat } from './KpiCard';

export { FloatsChart } from './FloatsChart';
export type { FloatsChartProps } from './FloatsChart';

export { ReportTable } from './ReportTable';
export type { ReportTableProps, ReportTableRow } from './ReportTable';

export { EmptyDashboard } from './EmptyDashboard';
export type { EmptyDashboardProps, EmptyDashboardTemplate } from './EmptyDashboard';

export { EmptyState } from './EmptyState';
export type { EmptyStateProps } from './EmptyState';

export { ResultCount } from './ResultCount';
export type { ResultCountProps } from './ResultCount';

export { ListFilterBar } from './ListFilterBar';
export type { ListFilterBarProps } from './ListFilterBar';

export { BlockTypePicker } from './BlockTypePicker';
export type { BlockTypePickerProps, BlockKind } from './BlockTypePicker';

export { DashboardListSidebar } from './DashboardListSidebar';
export type { DashboardListSidebarProps, DashboardListItem } from './DashboardListSidebar';

export { BlockWizard } from './BlockWizard';
export type { BlockWizardProps, BlockWizardProposal } from './BlockWizard';

// Chat Attachments
export { AttachmentChip } from './AttachmentChip';
export type { AttachmentChipProps, AttachmentChipStatus } from './AttachmentChip';
export { AttachmentTray } from './AttachmentTray';
export type { AttachmentTrayProps, AttachmentTrayItem, AttachmentTrayError } from './AttachmentTray';
export { MessageAttachment } from './MessageAttachment';
export type { MessageAttachmentProps } from './MessageAttachment';

// Product Attachments
export { AttachmentControl } from './AttachmentControl';
export type { AttachmentControlProps } from './AttachmentControl';
